
import { ReactNode } from "react";
import { PageLayout } from "@/components/layout/PageLayout";
import { BreadcrumbNav } from "@/components/case-studies/BreadcrumbNav";
import { CaseStudy } from "@/data/caseStudyTypes";
import { Container } from "@/components/ui/container";
import { CaseStudySchema } from "@/components/seo/CaseStudySchema";
import { BreadcrumbSchema } from "@/components/seo/BreadcrumbSchema";

interface CaseStudyLayoutProps {
  caseStudy: CaseStudy;
  children: ReactNode;
}

export function CaseStudyLayout({ caseStudy, children }: CaseStudyLayoutProps) {
  // Prepare SEO data
  const title = `${caseStudy.title} | edgefi Case Study`;
  const description = `${caseStudy.challenge} ${caseStudy.solution}`.substring(0, 155);
  const canonicalUrl = `/case-studies/${caseStudy.id}`;
  const keywords = [
    "case study", 
    caseStudy.industry, 
    "cybersecurity case study",
    "security transformation",
    ...(caseStudy.tags || [])
  ];

  // Prepare schema data
  const schemaData = {
    type: "case-study" as const,
    data: {
      title: caseStudy.title,
      description,
      url: `https://edgefi.com${canonicalUrl}`,
      industry: caseStudy.industry,
      companyType: caseStudy.companyType,
      challenge: caseStudy.challenge,
      solution: caseStudy.solution,
      results: caseStudy.results.map(r => r.text),
      datePublished: new Date().toISOString().split('T')[0],
    }
  };

  // Breadcrumb items for schema
  const breadcrumbItems = [
    { name: "Home", url: "/", position: 1 },
    { name: "Case Studies", url: "/case-studies", position: 2 },
    { name: caseStudy.title, url: canonicalUrl, position: 3 }
  ];

  return (
    <>
      {/* Add case study specific schema */}
      <CaseStudySchema
        title={caseStudy.title}
        description={description}
        url={canonicalUrl}
        datePublished={new Date().toISOString().split('T')[0]}
        industry={caseStudy.industry}
        companyType={caseStudy.companyType || ""}
        challenge={caseStudy.challenge}
        solution={caseStudy.solution}
        results={caseStudy.results.map(r => r.text)}
        testimonial={caseStudy.testimonial}
        image={caseStudy.imageUrl || "/og-image.png"}
      />

      {/* Add breadcrumb schema */}
      <BreadcrumbSchema items={breadcrumbItems} />

      <PageLayout
        title={title}
        description={description}
        canonicalUrl={canonicalUrl}
        ogType="article"
        keywords={keywords}
        schema={schemaData}
      >
        <Container>
          <BreadcrumbNav currentPage={caseStudy.title} />
          {children}
        </Container>
      </PageLayout>
    </>
  );
}
